import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, array, object, string } from 'yup';

export type SetupUserProviderRgsItem = {
  rgsId: string;
  providerIds: string[];
};

export type SetupUserFormState = {
  isProviderUsersAdmin: string | boolean;
  allowedRoleIds: string[] | string;
  email: string;
  password: string;
  providerRgs: SetupUserProviderRgsItem[];
  roleIds: string[] | string;
};

export const setupUserFormStateDefault: SetupUserFormState = {
  isProviderUsersAdmin: '',
  allowedRoleIds: [],
  email: '',
  password: '',
  providerRgs: [],
  roleIds: '',
};

export const setupUserSchemaDefault: ObjectSchema<Partial<SetupUserFormState>> = object<
  Partial<SetupUserFormState>
>().shape({
  isProviderUsersAdmin: string().optional(),
  allowedRoleIds: array().when('isProviderUsersAdmin', {
    is: 'true',
    then: (schema) => schema.required('allowed role ids is required field'),
    otherwise: (schema) => schema.notRequired(),
  }),
  email: string().email().required('email is required field'),
  password: string()
    .required('Password is required field')
    .test('no-whitespace', 'Password cannot contain spaces', function (value) {
      return !value || !/\s/.test(value);
    })
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[^a-zA-Z0-9]/, 'Password must contain at least one symbol')
    .test('password-not-contain-email', 'Password cannot contain the email', function (value) {
      const email = this.parent.email;
      if (!email || !value) return true;
      const trimmedPassword = value.replace(/\s/g, '').toLowerCase();
      const trimmedEmail = email.replace(/\s/g, '').toLowerCase();
      return !trimmedPassword.includes(trimmedEmail);
    }),
  roleIds: string().required('roleIds is required field'),
  providerRgs: array()
    .of(
      object({
        rgsId: string().required(),
        providerIds: array().of(string().required()).min(1).required(),
      })
    )
    .min(1)
    .required(),
});

type SetupUserFormContextType = {
  schema: ObjectSchema<Partial<SetupUserFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<SetupUserFormState>>>;
  methods?: UseFormReturn<SetupUserFormState>;
};

const createUserFormContextDefault = {
  schema: setupUserSchemaDefault,
  setSchema: noop,
} satisfies SetupUserFormContextType;

export const SetupUserFormContext = createContext<SetupUserFormContextType>(
  createUserFormContextDefault
);

export const useSetupUserForm = () => {
  const { methods } = useContext(SetupUserFormContext);
  if (!methods) {
    throw new Error('SetupUserFormContext is empty');
  }

  return methods;
};

export const useManageSetupUserSchema = () => {
  const { setSchema } = useContext(SetupUserFormContext);

  return { setSchema };
};
